import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { analysisApi } from '@/services/api/analysisApi';
import { FailureFilters, DEFAULT_FAILURE_FILTERS, OnyxDataFilter } from '@/types';

export type AnalysisMethod = 'johnson' | 'mle';

export interface MethodRecommendation {
  method: AnalysisMethod;
  reason: string;
  confidence: 'low' | 'medium' | 'high';
}

/**
 * Hook for real-time Weibull analysis with method selection support
 * Returns standard format
 */
export function useWeibullAnalysis(
  filters: FailureFilters = DEFAULT_FAILURE_FILTERS,
  confidenceLevel: number = 0.95,
  method?: AnalysisMethod,
  useONYXData = false,
  useONYXDataFilter = false,
  onyxFilters?: OnyxDataFilter,
  enabled = true
) {

  // When ONYX data is requested without an explicit ONYX filter, derive the
  // ONYX filter from the primary failure filter (matching the legacy behaviour).
  // The server applies the same defaulting, but we resolve it here too so the
  // value sent on the wire — and the cache key — are stable and explicit.
  if (useONYXData && !useONYXDataFilter) {
    onyxFilters = {
      componentsMajor: filters.componentsMajor,
      componentsMinor: filters.componentsMinor,
      turbineModels: filters.turbineModels
    };
  }

  // The Weibull(Failures) computation now runs server-side at
  // POST /api/v2/analysis/weibull (Phase 1B). This hook is a thin fetch over
  // that endpoint; the response envelope maps 1:1 to the hook's return
  // contract.
  //
  // We expose `isInitialLoading` as our `isLoading` so a disabled query
  // (enabled=false) does not register as "loading" — in v4 plain `isLoading`
  // stays true for disabled queries until data arrives, which would leave UI
  // spinners stuck on page mount.
  //
  // The client-only range/date filters (operationTimeMin/Max, dateFrom/To) are
  // now applied server-side, so — unlike the legacy client-compute path — they
  // are part of the request body and MUST be part of the cache key. Including
  // the whole `filters` object (rather than stripping the range fields) keeps
  // results that differ only by range from colliding in the cache.
  const {
    data: response,
    isInitialLoading: isLoading,
    error,
  } = useQuery({
    queryKey: [
      'weibull-analysis',
      filters,
      method,
      confidenceLevel,
      useONYXData,
      useONYXDataFilter,
      onyxFilters,
    ],
    queryFn: () =>
      analysisApi.getWeibullAnalysis(
        filters,
        confidenceLevel,
        method,
        useONYXData,
        useONYXDataFilter,
        onyxFilters,
      ),
    staleTime: 60000, // 1 minute
    cacheTime: 600000, // 10 minutes
    refetchOnWindowFocus: false,
    enabled,
  });

  // Honour the caller's explicit method first (matching the legacy
  // `method || recommendation?.method || 'mle'` precedence), then fall back to
  // the server-selected method, then the recommendation, then 'mle'. This keeps
  // the value stable before any response arrives and never overrides an
  // explicit caller choice once the response lands.
  const selectedMethod: AnalysisMethod =
    method ?? response?.selectedMethod ?? response?.recommendation?.method ?? 'mle';

  const diversityMetrics = response?.diversityMetrics ?? {
    uniqueSites: 0,
    uniqueTurbines: 0,
    uniqueComponents: 0,
  };

  return {
    analysis: response?.analysis ?? null,
    isLoading,
    error,
    hasEnoughData: response?.hasEnoughData ?? false,
    sampleSize: response?.sampleSize ?? 0,
    metadata: response?.metadata ?? null,
    diversityMetrics,
    selectedMethod,
    recommendation: response?.recommendation ?? null,
    supportingPlotData: {
      censorPlot: response?.supportingPlotData?.censorPlot,
      onyxCensorPlot: response?.supportingPlotData?.onyxCensorPlot,
    },
  };
}

/**
 * Hook for component-specific Weibull analysis with multi-select components
 */
export function useComponentWeibullAnalysis(
  filters: FailureFilters = DEFAULT_FAILURE_FILTERS,
  components: string[]
) {
  const componentFilters: FailureFilters = {
    ...filters,
    componentsMajor: components,
  };

  return useWeibullAnalysis(componentFilters, 0.95);
}

/**
 * Hook for site-specific Weibull analysis with multi-select sites
 */
export function useSiteWeibullAnalysis(
  filters: FailureFilters = DEFAULT_FAILURE_FILTERS,
  sites: string[]
) {
  const siteFilters: FailureFilters = {
    ...filters,
    sites: sites,
  };

  return useWeibullAnalysis(siteFilters, 0.95);
}

/**
 * Hook for comparative Weibull analysis between different filter sets
 */
export function useComparativeWeibullAnalysis(filterSets: FailureFilters[]) {
  // Use a static approach to avoid hook call in callback
  const analysis1 = useWeibullAnalysis(filterSets[0] || DEFAULT_FAILURE_FILTERS, 0.95);
  const analysis2 = useWeibullAnalysis(filterSets[1] || DEFAULT_FAILURE_FILTERS, 0.95);
  const analysis3 = useWeibullAnalysis(filterSets[2] || DEFAULT_FAILURE_FILTERS, 0.95);

  // Collect only the analyses for the provided filter sets
  const analyses = [
    filterSets[0] ? analysis1 : null,
    filterSets[1] ? analysis2 : null,
    filterSets[2] ? analysis3 : null,
  ].filter(Boolean);

  return {
    analyses: analyses.map((a) => a.analysis).filter((a) => a !== null),
    isLoading: analyses.some((a) => a.isLoading),
    hasErrors: analyses.some((a) => a.error),
    validAnalyses: analyses.filter((a) => a.hasEnoughData).length,
    totalSampleSize: analyses.reduce((sum, a) => sum + a.sampleSize, 0),
    metadata: analyses.map((a) => a.metadata).filter(Boolean),
  };
}

/**
 * Hook for filtered Weibull analysis with automatic data quality checks
 */
export function useQualityFilteredWeibullAnalysis(
  filters: FailureFilters = DEFAULT_FAILURE_FILTERS,
  qualityOptions: {
    minOperationTime?: number;
    maxOperationTime?: number;
    excludeZeroTimes?: boolean;
    minSampleSize?: number;
  } = {}
) {
  // Apply quality filters
  const qualityFilters: FailureFilters = {
    ...filters,
    operationTimeMin: qualityOptions.minOperationTime || filters.operationTimeMin,
    operationTimeMax: qualityOptions.maxOperationTime || filters.operationTimeMax,
  };

  const { analysis, isLoading, error, hasEnoughData, sampleSize, metadata } = useWeibullAnalysis(
    qualityFilters,
    0.95
  );

  // Additional quality checks
  const qualityMetrics = useMemo(() => {
    if (!analysis) return null;

    const operationTimes = analysis.plotData.operationTimes;

    const zeroCount = operationTimes.filter((t) => t === 0).length;
    const outliers = operationTimes.filter((t) => {
      const q1 = operationTimes[Math.floor(operationTimes.length * 0.25)];
      const q3 = operationTimes[Math.floor(operationTimes.length * 0.75)];
      const iqr = q3 - q1;
      return t < q1 - 1.5 * iqr || t > q3 + 1.5 * iqr;
    }).length;

    return {
      zeroCount,
      outlierCount: outliers,
      dataQuality:
        analysis.parameters.rSquared > 0.9
          ? 'excellent'
          : analysis.parameters.rSquared > 0.8
            ? 'good'
            : analysis.parameters.rSquared > 0.7
              ? 'fair'
              : 'poor',
      sampleSize: operationTimes.length,
      meetsMinSample: operationTimes.length >= (qualityOptions.minSampleSize || 3),
    };
  }, [analysis, qualityOptions.minSampleSize]);

  return {
    analysis,
    isLoading,
    error,
    hasEnoughData: hasEnoughData && (qualityMetrics?.meetsMinSample ?? false),
    sampleSize,
    qualityMetrics,
    metadata,
  };
}
